import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { ArrowLeft, Bookmark, ChevronRight, LogOut, MapPin, Moon, Settings, SlidersHorizontal, UserRound } from "lucide-react";
import { useOrigin } from "../context/OriginContext";
import { useTheme } from "../context/ThemeContext";
import { clearGatewaySession } from "../services/api";
import { profileTabPath } from "../utilities/profileTabs";
import { usePopover, type PopoverPlacement } from "../utilities/usePopover";
import { LocationPanel, originTone } from "./LocationPicker";
import styles from "./AccountMenu.module.scss";

const initialsOf = (name?: string) =>
    (name || '')
        .split(/[\s@.]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0].toUpperCase())
        .join('') || '?';

/** The signed-in user's picture, or their initials when there isn't one. Fills its parent. */
export const AccountAvatar = () => {
    const { user } = useAuth0();
    return user?.picture
        ? <img className={styles.avatarImg} src={user.picture} alt="" referrerPolicy="no-referrer" />
        : <span className={styles.avatarInitials}>{initialsOf(user?.name || user?.email)}</span>;
};

const LINKS = [
    { to: '/profile', label: 'Profile', Icon: UserRound },
    { to: profileTabPath('saved'), label: 'Saved', Icon: Bookmark },
    { to: profileTabPath('preferences'), label: 'Preferences', Icon: SlidersHorizontal },
    { to: profileTabPath('settings'), label: 'Settings', Icon: Settings },
];

const ITEM_SELECTOR = '[role="menuitem"], [role="menuitemcheckbox"]';

type AccountMenuProps = {
    /** What the trigger shows — an avatar, a "···" icon. */
    children: React.ReactNode;
    triggerClassName?: string;
    placement?: PopoverPlacement;
};

/**
 * Everything about the signed-in traveller in one menu: who they are,
 * where they're travelling from, their profile tabs, the theme and
 * signing out. Opens from the header avatar and the chat sidebar's card.
 */
export const AccountMenu = ({ children, triggerClassName = '', placement = 'below-end' }: AccountMenuProps) => {
    const { user, logout } = useAuth0();
    const { origin, loading } = useOrigin();
    const { theme, toggleTheme } = useTheme();
    const { open, toggle, close, triggerRef, popoverRef, position } = usePopover({ width: 288, placement });
    const [view, setView] = useState<'menu' | 'location'>('menu');
    const menuRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!open) setView('menu');
    }, [open]);

    // Focus lands on the first item, so the arrow keys work straight away.
    useEffect(() => {
        if (open && view === 'menu') menuRef.current?.querySelector<HTMLElement>(ITEM_SELECTOR)?.focus();
    }, [open, view]);

    const onMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>(ITEM_SELECTOR) ?? []);
        if (!items.length) return;
        const index = items.indexOf(document.activeElement as HTMLElement);
        const focusAt = (i: number) => items[(i + items.length) % items.length].focus();
        if (event.key === 'ArrowDown') focusAt(index + 1);
        else if (event.key === 'ArrowUp') focusAt(index - 1);
        else if (event.key === 'Home') focusAt(0);
        else if (event.key === 'End') focusAt(items.length - 1);
        else if (event.key === 'Tab') close(false);
        else return;
        if (event.key !== 'Tab') event.preventDefault();
    };

    const name = user?.name || user?.nickname || 'Signed in';
    const isGuess = originTone(origin, loading) === 'guess';

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                className={triggerClassName}
                onClick={toggle}
                aria-label="Account menu"
                aria-haspopup="menu"
                aria-expanded={open}
            >
                {children}
            </button>
            {open && position && createPortal(
                <div ref={popoverRef} className={styles.popover} style={{ ...position }}>
                    {view === 'location' ? (
                        <div role="dialog" aria-label="Your location">
                            <button type="button" className={styles.back} onClick={() => setView('menu')}>
                                <ArrowLeft size={15} aria-hidden="true" /> Back
                            </button>
                            <LocationPanel onDone={() => setView('menu')} />
                        </div>
                    ) : (
                        <div ref={menuRef} role="menu" aria-label="Account" onKeyDown={onMenuKeyDown}>
                            <div className={styles.identity}>
                                <span className={styles.identityAvatar}><AccountAvatar /></span>
                                <span className={styles.identityText}>
                                    <span className={styles.name}>{name}</span>
                                    {user?.email && user.email !== name && (
                                        <span className={styles.email}>{user.email}</span>
                                    )}
                                </span>
                            </div>

                            <button
                                type="button"
                                role="menuitem"
                                className={`${styles.item} ${styles.locationItem}`}
                                onClick={() => setView('location')}
                            >
                                <span className={styles.locationIcon}>
                                    <MapPin size={16} aria-hidden="true" />
                                    {isGuess && <span className={styles.guessDot} aria-hidden="true" />}
                                </span>
                                <span className={styles.locationText}>
                                    <span className={styles.locationLabel}>Travelling from</span>
                                    <span className={styles.locationCity}>{origin?.city || 'Set your location'}</span>
                                </span>
                                <ChevronRight size={16} className={styles.chevron} aria-hidden="true" />
                            </button>

                            <div className={styles.divider} role="separator" />

                            {LINKS.map(({ to, label, Icon }) => (
                                <Link key={to} to={to} role="menuitem" className={styles.item} onClick={() => close(false)}>
                                    <Icon size={16} aria-hidden="true" /> {label}
                                </Link>
                            ))}

                            <button
                                type="button"
                                role="menuitemcheckbox"
                                aria-checked={theme === 'dark'}
                                className={styles.item}
                                onClick={toggleTheme}
                            >
                                <Moon size={16} aria-hidden="true" /> Dark mode
                                <span className={styles.switch} aria-hidden="true"><span className={styles.switchKnob} /></span>
                            </button>

                            <div className={styles.divider} role="separator" />

                            <button
                                type="button"
                                role="menuitem"
                                className={styles.item}
                                onClick={() => {
                                    clearGatewaySession();
                                    logout({ logoutParams: { returnTo: `${window.location.origin}/chat` } });
                                }}
                            >
                                <LogOut size={16} aria-hidden="true" /> Sign out
                            </button>
                        </div>
                    )}
                </div>,
                document.body,
            )}
        </>
    );
};
